import { useEffect, useState } from "react";
import WorkLogReport from "../models/WorkLogReport";
import OpsDateUtils from "../utils/OpsDateUtils";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import WorkLogWeeklyService from "../services/WorkLogWeeklyService";
import { SnackBarData } from "../external/ensolvers-core-frontend-mui/types/SnackbarData";
import { sendSnackBarMessage } from "../utils/SnackBarUtils";

interface UseWeeklyValidationParams {
  instance: WorkLogReport;
  date: Date;
  week: string;
  setRows: React.Dispatch<React.SetStateAction<WorkLogReport[]>>;
}

const useWeeklyValidation = ({ instance, date, week, setRows }: UseWeeklyValidationParams) => {
  const [buttonChecked, setButtonChecked] = useState<boolean>(instance.validated[week]);

  useEffect(() => setButtonChecked(instance.validated[week]), [instance]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateValidatedWeekByRowExternalId = (assignationExternalId: string | undefined, week: string, validated: boolean) => {
    setRows((prev) =>
      prev.map((row) =>
        row.assignationExternalId === assignationExternalId
          ? {
              ...row,
              validated: {
                ...row.validated,
                [week]: validated,
              },
            }
          : row,
      ),
    );
  };

  const handleChange = async (checked: boolean) => {
    let shortWeekFirstDay = OpsDateUtils.getFirstDayOfWeekInMonth(date, parseInt(week) - 1);
    let utcDate = OpsDateUtils.toUTC(shortWeekFirstDay);

    try {
      //Optimistic update handler
      checkboxUpdateHandler(true, checked);

      // Try performing the approveWeek operation
      const response = await WorkLogWeeklyService.approveWeek({
        assignationExternalId: instance.assignationExternalId,
        shortWeekFirstDay: utcDate,
        validated: checked,
      });

      //The report is locked by manager
      if (response.getContentOrThrowError().validated !== checked) {
        const snackData: SnackBarData = { text: "Cannot update. This report had been locked.", severity: "error" };
        sendSnackBarMessage(snackData);
        //Revert the optimistic update
        checkboxUpdateHandler(false, checked);
      }
    } catch (error: any) {
      // Error catching section (Missing worklogs, not enough permissions, etc)
      if (error && error.apiResponse && error.apiResponse.message) {
        NotificationService.error(error.apiResponse.message);
      } else {
        NotificationService.error("Unexpected error at checking worklog");
      }
      //Revert the optimistic update
      checkboxUpdateHandler(false, checked);
    }
  };

  const checkboxUpdateHandler = (success: boolean, checked: boolean) => {
    if (success) {
      //Optimistic update of the UI
      setButtonChecked(checked);
      updateValidatedWeekByRowExternalId(instance.assignationExternalId, week, checked);
    } else {
      //Revert changes in case of error or blocker
      setButtonChecked(!checked);
      updateValidatedWeekByRowExternalId(instance.assignationExternalId, week, !checked);
    }
  };

  return {
    buttonChecked,
    handleChange,
  };
};

export default useWeeklyValidation;
